import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Link, useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import cn from "classnames";
import Tabs from "./components/tabs";
import Filter from "../saved/filter";
import Profiles from "../saved/profiles";
import Organizations from "../saved/organizations";
import Events from "../saved/events";
import Jobs from "../saved/jobs";
import FollowingModal from "./components/followingModal";
import FollowingSidebar from "./components/followingSidebar";
import FeedScopeChip from "./components/feedScopeChip";
import QuestionForm from "./components/questionForm";
import QuestionFormSuccess from "./components/questionFormSuccess";
import { ReactComponent as PlusIcon } from "../../media/icons/plus.svg";
import { ReactComponent as EmptyIcon } from "../../media/icons/new-empty-listing.svg";
import { ReactComponent as SearchIcon } from "../../media/icons/search.svg";
import { ReactComponent as PencilIcon } from "../../media/icons/edit.svg";
import { actions as homeActions } from "../../actions/home";
import { actions as searchActions } from "../../actions/search";
import { actions as followingActions } from "../../actions/following";
import { OverlayPreloader } from "../common/preloader";
import PageNames from "../pageNames";
import { useDebounce, useDocTitle } from "../../helpers/hooks";
import { actions as actionProfile } from "../../actions/account/profile";
import { modalsName } from "../../constants/account";
import { Notification } from "../common/notification";
import { Pagination } from "../common/pagination";
import styles from "./feed.module.css";
import { getQuestionFormInitialValues, markQuestionFormAnswered, shouldShowQuestionForm } from "../../helpers/questionForm";
import { buildCommunityGroups, communityTitles } from "../../helpers/followingView";
import { countFollowedCommunities } from "../../helpers/feedSidebar";
import { FEED_V2_ENABLED } from "../../constants/featureFlags";
import { feedReturnState } from "../../helpers/feedReturn";
import FeedStream from "./components/feedStream";
import FeedHeader from "./components/feedHeader";
import FeedSearchHeader from "./components/feedSearchHeader";
import FeedTypeChips from "./components/feedTypeChips";
import useDeepLinkedType from "./components/feedTypeChips/useDeepLinkedType";
import useFeedSearchQuery from "./useFeedSearchQuery";
import UpcomingEventsStrip from "./components/upcomingEventsStrip";
import NeedsAttention from "./components/needsAttention";

/**
 * v2 stacks the sidebar cards: the "Show" type filter (GROWTH-7573) on top, then the Following card's own column with
 * "Needs your attention" under it (GROWTH-7133). v1 has neither extra card and keeps the Following card as its own flex
 * child.
 *
 * @param {Object} props
 * @param {boolean} props.isV2 FEED_V2_ENABLED
 * @param {JSX.Element} props.above the card rendered before `children`, in v2 only
 * @param {JSX.Element} props.below the card rendered after `children`, in v2 only
 * @param {JSX.Element} props.children the Following card
 */
const SidebarColumn = ({ isV2, above, below, children }) =>
  isV2 ? (
    <div className={styles.sidebarStack}>
      {above}
      <div className={styles.sidebarV2}>
        {children}
        {below}
      </div>
    </div>
  ) : (
    children
  );

const Feed = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);
  const wrapperRef = useRef(null);
  const [filter, setFilter] = useState({ value: "", label: "" });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);
  const [followingSuccessText, setFollowingSuccessText] = useState(null);
  // The sidebar's selected community (GROWTH-7130); null = "All communities". FEED_V2_ENABLED only. Seeded from the link
  // state of a listing page's "My Feed" breadcrumb (GROWTH-7576), so the member returns to the community they had chosen.
  const [communityId, setCommunityId] = useState(() =>
    Number.isInteger(location.state?.feedCommunityId) ? location.state.feedCommunityId : null
  );
  // The type filter (GROWTH-7573): the sidebar's "Show" card, or the chip row below 1024px; seeded from ?type=. v2 only.
  const [feedType, setFeedType] = useDeepLinkedType(FEED_V2_ENABLED);
  // The header search applied to the feed (GROWTH-7577): `/?q=…&type=…`. v2 only; clearing it goes back to the plain feed.
  const feedQuery = useFeedSearchQuery();
  const handleClearSearch = useCallback(() => navigate({ pathname: location.pathname || "/" }), [navigate, location.pathname]);
  // What a card in the events panel carries to the listing page, for its "My Feed" breadcrumb (GROWTH-7576).
  const feedLinkState = useMemo(() => feedReturnState({ type: feedType, communityId }), [feedType, communityId]);
  // Set after a change on /following; asks My Feed for a fresh (not browser-cached) list.
  const { changedAt, scope } = useSelector(state => state.following);
  const getMyFeed = useCallback(
    (command, searchTerm, page, limit) => dispatch(homeActions.getMyFeed(command, searchTerm, page, limit, changedAt)),
    [dispatch, changedAt]
  );
  const [page, setPage] = useState(1);
  const [perPageFeed, setPerPageFeed] = useState("");
  const { setSearchTerm: setSearchDebounceTerm } = useDebounce(setDebouncedSearch, false, 500);
  const { professionTitle, professions } = useSelector(state => state.search);
  const { communities: communitiesTree } = useSelector(state => state.main);

  const { myFeed, feedPagination, preloaders = {}, followings = [], error, feedStream } = useSelector(state => state.home);
  const { profile, modals } = useSelector(state => state.account);
  const addToFavorite = useCallback(listingId => dispatch(homeActions.addToFavorite(listingId)), [dispatch]);
  const removeAllError = useCallback(() => dispatch(homeActions.removeAllError()), [dispatch]);
  const removeFavorite = useCallback(listingId => dispatch(homeActions.removeFavorite(listingId)), [dispatch]);
  const getFollowing = useCallback(() => dispatch(homeActions.getFollowing()), [dispatch]);
  const getScope = useCallback(() => dispatch(followingActions.getScope()), [dispatch]);
  // v1 only: v2's Manage is the /following page (GROWTH-7216).
  const removeFollowing = useCallback(id => dispatch(homeActions.removeFollowing(id)), [dispatch]);
  const toggleActivityOfModal = useCallback((modal, isActive) => dispatch(actionProfile.toggleActivityOfModal(modal, isActive)), [
    dispatch
  ]);

  // Titles for the sidebar's community rows (GROWTH-7130) — the scope only carries ids.
  const communityTitleById = useMemo(() => communityTitles(buildCommunityGroups(communitiesTree)), [communitiesTree]);
  const selectedCommunityName = communityId !== null ? communityTitleById.get(communityId) : null;

  const groupedListings = useMemo(() => {
    const values = Object.values(myFeed?.list || {});
    return values.reduce((acc, item) => {
      const type = item?.listing_type?.toLowerCase?.();
      if (!type) return acc;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {});
  }, [myFeed]);

  const makePaginationLinks = () => {
    const navLinks = [];
    const numberOfPages = feedPagination.total_pages || 0;
    const pathname = location.pathname || "/";

    for (let i = 1; i <= numberOfPages; i++) {
      navLinks.push({ pathname, search: queryString.stringify({ page: i }), number: i });
    }

    return navLinks;
  };

  const handlePerPage = feedPerPage => {
    setPerPageFeed(feedPerPage);
    const pathname = location.pathname || "/";
    navigate(
      {
        pathname,
        search: queryString.stringify({ page: 0 })
      },
      {
        state: { number: 0 }
      }
    );
  };

  const clearAllModals = useCallback(() => dispatch(actionProfile.clearAllModals()), [dispatch]);
  const lastScrollTop = useRef(0);

  const all = { value: "All", label: "all" };
  const activeOptions = professionTitle.map(item => ({
    value: item,
    label: item.toLowerCase() === "people" ? "profiles" : item.toLowerCase()
  }));
  const profiles = activeOptions.find(item => item.label.includes("profile"));
  const organizations = activeOptions.find(item => item.label.includes("organization"));
  const jobs = activeOptions.find(item => item.label.includes("job"));
  const events = activeOptions.find(item => item.label.includes("event"));

  const filterOptions = [all, profiles, organizations, jobs, events];

  const isShowQuestionForm = useMemo(() => shouldShowQuestionForm(profile), [
    profile.user_id,
    profile.email_verified_at,
    profile.birth_date
  ]);
  const questionFormInitialValues = useMemo(() => getQuestionFormInitialValues(profile), [
    profile.name_f,
    profile.name_l,
    profile.birth_date,
    profile.country,
    profile.state,
    profile.city,
    profile.zip
  ]);

  const handleShowModal = () => {
    setIsShowModal(true);
  };

  const handleHideModal = () => {
    setIsShowModal(false);
  };

  const handleChangeTab = (value, isScroll) => {
    if (value.label !== filter.label) {
      setFilter(value);

      const pathname = location.pathname || "/";
      navigate({ pathname });
      if (page !== 1) {
        setPage(1);
      }
    }

    if (isScroll && wrapperRef.current) {
      wrapperRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleChange = event => {
    setSearch(event.target.value);
  };

  const handleReset = () => {
    setSearch("");
  };

  const handleCloseFormModal = () => {
    if (modals.questionForm) {
      markQuestionFormAnswered(profile.user_id);
    }
    clearAllModals();
  };

  const fetchData = () => {
    if (filter.value !== all.value) {
      if (loading) {
        return;
      }

      document.body.style.overflow = "hidden";
      setLoading(true);

      getMyFeed(filter.label, search, page + 1).then(() => {
        setPage(prev => prev + 1);
        setLoading(false);
        document.body.style.overflow = "";
      });
    }
  };

  useEffect(() => {
    if (!filter.value) {
      setFilter(all);
    }

    getFollowing();
  }, []);

  useEffect(() => {
    // The sidebar's community list (GROWTH-7130) has no v1 equivalent — nothing here reads
    // `scope` when the flag is off. Reloaded on a following-page change (`changedAt`), same as
    // `getFollowing`/`getMyFeed` above, so an updated community set shows up without a full reload.
    if (!FEED_V2_ENABLED) return;
    getScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedAt]);

  const scopeIds = scope.communities?.heritage_ids;
  const previousScopeIds = useRef(scopeIds);
  useEffect(() => {
    // A selected community that just dropped out of the followed set (a /following change saved
    // after this page loaded, GROWTH-7102) can't stay selected. Only a drop resets it — a
    // selection that was never in the set (made from a card's attribution row) is left alone.
    const previous = previousScopeIds.current || [];
    const current = scopeIds || [];
    if (communityId !== null && previous.includes(communityId) && !current.includes(communityId)) {
      setCommunityId(null);
    }
    previousScopeIds.current = scopeIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeIds]);

  useEffect(() => {
    // The old /my-feed profession-tree filter has no v2 equivalent — FeedTypeChips is a fixed list.
    if (FEED_V2_ENABLED) return;
    if (!professions?.length) {
      dispatch(searchActions.getProfessionsTree());
    }
  }, [dispatch, professions?.length]);

  useEffect(() => {
    // FeedStream (mode="feed") owns its own fetch against GET /api-v2/feed when v2 is on.
    if (FEED_V2_ENABLED) return;
    const { page: queryPage } = queryString.parse(location.search);
    const numericPage = parseInt(queryPage || 1, 10);
    if (!filter.label) return;

    setPage(numericPage);
    getMyFeed(filter.label, debouncedSearch, numericPage, perPageFeed);
    // changedAt: a community change saved on the way out of /following can land after this page loaded (GROWTH-7102).
  }, [location.search, filter.label, debouncedSearch, perPageFeed, changedAt]);

  useEffect(() => {
    if (isShowQuestionForm) {
      toggleActivityOfModal(modalsName.questionForm, true);
    }
  }, [isShowQuestionForm, profile.added]);

  useEffect(() => {
    setSearchDebounceTerm(search);
  }, [search]);

  const shouldShowPagination = useMemo(() => {
    const totalPages = feedPagination.total_pages || 0;
    const currentPage = feedPagination.current_page || 1;
    const perPage = feedPagination.records_per_page || 10;
    const totalItems = Object.values(groupedListings).flat().length;

    return totalPages > 1 || currentPage > 1 || totalItems >= perPage;
  }, [feedPagination, groupedListings]);

  useDocTitle("dashboard_myfeed_metatitle");

  useEffect(() => {
    // v1-only infinite scroll; FeedStream's "Show older" button replaces this in v2.
    if (FEED_V2_ENABLED) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (page < feedPagination.total_pages) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        if (entries[0].isIntersecting && scrollTop > lastScrollTop.current) {
          fetchData();
        }

        lastScrollTop.current = scrollTop;
      }
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [filter.value, page, feedPagination.total_pages]);

  const isShowContent = useMemo(() => {
    return filter.value !== all.value || search.trim() !== "" || Object.values(groupedListings).some(arr => arr.length > 0);
  }, [groupedListings, filter.value, search]);

  const isShowFollowingContent = useMemo(() => followings.length, [followings.length]);
  // v2 counts the communities the sidebar lists, none when the member follows all of them
  // (GROWTH-7216); v1 keeps the stored follow-row count.
  const followingCount = FEED_V2_ENABLED ? countFollowedCommunities(scope.communities) : followings.length;

  return (
    <>
      {modals.questionForm ? <QuestionForm closeModal={handleCloseFormModal} formValues={questionFormInitialValues} /> : null}
      {modals.questionFormSuccess && <QuestionFormSuccess clearAllModals={clearAllModals} />}

      {error.favorites && (
        <Notification error close={removeAllError} time={5000}>
          {t("dashboard_feed_favorites_error")}
        </Notification>
      )}
      {followingSuccessText && (
        <Notification success close={() => setFollowingSuccessText(null)} time={5000}>
          {followingSuccessText}
        </Notification>
      )}

      <div className={cn(styles.wrapper, { [styles.wrapperV2]: FEED_V2_ENABLED })} ref={wrapperRef}>
        {preloaders.feed && <OverlayPreloader />}
        {FEED_V2_ENABLED ? null : <PageNames />}
        {!FEED_V2_ENABLED && isShowModal && (
          <FollowingModal
            removePreloader={preloaders.removeFollowing}
            followings={followings}
            removeFollowing={removeFollowing}
            onSuccess={setFollowingSuccessText}
            onClose={handleHideModal}
          />
        )}
        {FEED_V2_ENABLED ? (
          <div className={styles.headerRow}>
            {FEED_V2_ENABLED && feedQuery ? (
              <FeedSearchHeader
                query={feedQuery}
                total={feedStream.total}
                communityName={selectedCommunityName}
                onClear={handleClearSearch}
              />
            ) : (
              <FeedHeader firstName={profile.name_f} lastSeenAt={feedStream.lastSeenAt} newCount={feedStream.newCount} />
            )}
            <Tabs className={styles.headerTabs} />
          </div>
        ) : (
          <Tabs />
        )}
        {FEED_V2_ENABLED && !feedQuery ? (
          <UpcomingEventsStrip communityId={communityId} linkState={feedLinkState} className={styles.eventsPanel} />
        ) : null}
        {!FEED_V2_ENABLED && isShowContent ? (
          <>
            <div className={styles.searchContent}>
              <SearchIcon className={styles.searchIcon} />
              <input type="text" placeholder="Search" className={styles.search} value={search} onChange={handleChange} />
              {search ? (
                <button type="button" className={styles.reset} onClick={handleReset}>
                  Reset
                </button>
              ) : null}
            </div>

            <Filter filter={filter} setFilter={handleChangeTab} filterOptions={filterOptions.filter(item => item)} />
          </>
        ) : null}

        <div className={cn(styles.flex, { [styles.flexV2]: FEED_V2_ENABLED })}>
          <SidebarColumn
            isV2={FEED_V2_ENABLED}
            above={
              <FeedTypeChips
                variant="list"
                value={feedType}
                onChange={setFeedType}
                typeCounts={feedStream.typeCounts}
                className={styles.filterWide}
              />
            }
            below={<NeedsAttention />}
          >
            <div className={styles.following}>
              <div className={styles.followingHeading}>
                <h6 className={styles.followingTitle}>Following {followingCount ? `(${followingCount})` : ""}</h6>

                {isShowFollowingContent ? (
                  FEED_V2_ENABLED ? (
                    <Link to="/following" className={styles.followingEdit} aria-label={t("dashboard_feed_manage_following_aria")}>
                      <PencilIcon aria-hidden="true" />
                    </Link>
                  ) : (
                    <button type="button" className={styles.followingEdit} onClick={handleShowModal}>
                      <PencilIcon />
                    </button>
                  )
                ) : null}
              </div>

              {isShowFollowingContent ? (
                FEED_V2_ENABLED ? (
                  <FollowingSidebar
                    communities={scope.communities}
                    titles={communityTitleById}
                    newByCommunity={feedStream.newByCommunity}
                    newCount={feedStream.newCount}
                    visitUrls={scope.visit_urls}
                    communityId={communityId}
                    onSelect={setCommunityId}
                  />
                ) : (
                  <div className={styles.followingList}>
                    {followings.map(item => {
                      return (
                        <a rel="noopener noreferrer" href={item.url} target="_blank" key={item.name} className={styles.followingItem}>
                          {item.name}
                        </a>
                      );
                    })}
                  </div>
                )
              ) : null}
              <Link to="/following" className={cn(styles.followingButton, styles.followingLink)}>
                <PlusIcon />
                Follow Communities
              </Link>
            </div>
          </SidebarColumn>

          {FEED_V2_ENABLED ? (
            <div className={styles.content}>
              <FeedTypeChips value={feedType} onChange={setFeedType} className={styles.filterNarrow} />
              {Number.isFinite(feedStream.total) || selectedCommunityName ? (
                <div className={styles.streamBar}>
                  {Number.isFinite(feedStream.total) && !feedQuery ? (
                    <span className={styles.resultLine}>
                      {Number.isFinite(feedStream.listLimit) && feedStream.total > feedStream.listLimit
                        ? t("dashboard_feed_result_count_capped", { limit: feedStream.listLimit, total: feedStream.total })
                        : t("dashboard_feed_result_count", { count: feedStream.total })}
                    </span>
                  ) : null}
                  <FeedScopeChip communityName={selectedCommunityName} onClear={() => setCommunityId(null)} />
                </div>
              ) : null}
              <FeedStream mode="feed" type={feedType} communityId={communityId} onSelectCommunity={setCommunityId} q={feedQuery} />
            </div>
          ) : isShowContent ? (
            <div className={styles.content}>
              {(filter.value === profiles?.value || filter.value === all.value) && (
                <div className={styles.container}>
                  <Profiles
                    isExpand
                    onClick={() => handleChangeTab(profiles)}
                    isActive={filter.value === profiles?.value}
                    count={myFeed?.count?.profiles}
                    list={groupedListings.profiles}
                    removeFavorite={removeFavorite}
                    addToFavorite={addToFavorite}
                  />

                  {filter.value === profiles?.value && !groupedListings?.profiles?.length ? (
                    <div className={styles.emptyContentImage}>
                      <EmptyIcon className={styles.icon} />
                    </div>
                  ) : null}
                </div>
              )}
              {(filter.value === organizations?.value || filter.value === all.value) && (
                <div className={styles.container}>
                  <Organizations
                    isExpand
                    onClick={() => handleChangeTab(organizations)}
                    isActive={filter.value === organizations?.value}
                    count={myFeed?.count?.organizations}
                    list={groupedListings.organizations}
                    removeFavorite={removeFavorite}
                    addToFavorite={addToFavorite}
                    isOrganizations
                  />

                  {filter.value === organizations?.value && !groupedListings?.organizations?.length ? (
                    <div className={styles.emptyContentImage}>
                      <EmptyIcon className={styles.icon} />
                    </div>
                  ) : null}
                </div>
              )}
              {(filter.value === events?.value || filter.value === all.value) && (
                <div className={styles.container}>
                  <Events
                    isExpand
                    onClick={() => handleChangeTab(events)}
                    isActive={filter.value === events?.value}
                    count={myFeed?.count?.events}
                    list={groupedListings.events}
                    removeFavorite={removeFavorite}
                    addToFavorite={addToFavorite}
                  />

                  {filter.value === events?.value && !groupedListings?.events?.length ? (
                    <div className={styles.emptyContentImage}>
                      <EmptyIcon className={styles.icon} />
                    </div>
                  ) : null}
                </div>
              )}
              {(filter.value === jobs?.value || filter.value === all.value) && (
                <div className={styles.container}>
                  <Jobs
                    isExpand
                    onClick={() => handleChangeTab(jobs)}
                    isActive={filter.value === jobs?.value}
                    organizations
                    count={myFeed?.count?.jobs}
                    list={groupedListings.jobs}
                    removeFavorite={removeFavorite}
                    addToFavorite={addToFavorite}
                  />

                  {filter.value === jobs?.value && !groupedListings?.jobs?.length ? (
                    <div className={styles.emptyContentImage}>
                      <EmptyIcon className={styles.icon} />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.emptyContent}>
              <div className={styles.emptyContentImage}>
                <EmptyIcon className={styles.icon} />
              </div>
              <h1 className={styles.emptyTitle}>Your Feed is Looking a Little Empty</h1>
              <p className={styles.emptyDescription}>
                Follow people, communities, or jobs that connect you to your interests and heritage.
              </p>
            </div>
          )}
        </div>
      </div>
      {!FEED_V2_ENABLED && shouldShowPagination && (
        <Pagination
          className={styles.feedPagination}
          perPage={feedPagination.records_per_page}
          handlePerPage={handlePerPage}
          links={makePaginationLinks()}
          currentPage={feedPagination.current_page}
          lastPage={feedPagination.total_pages}
          isStartFromFirst
        />
      )}
      {!FEED_V2_ENABLED && <div ref={loaderRef} style={{ height: "50px" }} />}
    </>
  );
};

export default Feed;
